/**
 * AnnouncementsFeed - Dashboard tile showing active campaigns/announcements
 *
 * Mobile-optimized version for athlete-web app.
 */

import { useState, useEffect } from 'react';
import {
  Megaphone,
  X,
  ChevronRight,
  Gift,
  AlertCircle,
  Info,
  Sparkles,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import {
  getActiveCampaigns,
  recordCampaignView,
  recordCampaignClick,
  recordCampaignDismiss,
  type Campaign,
  type CampaignTrigger,
} from '@tss/shared';

interface AnnouncementsFeedProps {
  trigger?: CampaignTrigger;
  maxItems?: number;
}

export function AnnouncementsFeed({
  trigger = 'login',
  maxItems = 5,
}: AnnouncementsFeedProps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadCampaigns();
  }, [trigger]);

  async function loadCampaigns() {
    setLoading(true);
    try {
      const { campaigns: data } = await getActiveCampaigns(trigger, undefined, maxItems);
      setCampaigns(data);

      // Record views
      for (const campaign of data) {
        try {
          await recordCampaignView(campaign.campaign_id);
        } catch {
          // Ignore
        }
      }
    } catch (err) {
      console.error('Failed to load announcements:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDismiss(campaignId: string) {
    setDismissedIds(prev => new Set([...prev, campaignId]));
    try {
      await recordCampaignDismiss(campaignId);
    } catch {
      // Ignore
    }
  }

  async function handleCTAClick(campaign: Campaign) {
    try {
      await recordCampaignClick(campaign.campaign_id);
    } catch {
      // Ignore
    }

    if (campaign.cta_url) {
      if (campaign.cta_url.startsWith('http')) {
        window.open(campaign.cta_url, '_blank');
      } else {
        window.location.href = campaign.cta_url;
      }
    }
  }

  function getThemeStyles(theme: string) {
    switch (theme) {
      case 'celebration':
        return { icon: Gift, color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.2)' };
      case 'urgent':
        return { icon: AlertCircle, color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.2)' };
      case 'info':
        return { icon: Info, color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.2)' };
      default:
        return { icon: Sparkles, color: '#f97316', bg: 'rgba(249, 115, 22, 0.1)', border: 'rgba(249, 115, 22, 0.2)' };
    }
  }

  const visibleCampaigns = campaigns.filter(c => !dismissedIds.has(c.campaign_id));

  if (!loading && visibleCampaigns.length === 0) {
    return null;
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      padding: '16px',
      marginBottom: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
      }}>
        <h3 style={{
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '15px',
          fontWeight: 600,
          color: '#1e293b',
        }}>
          <Megaphone size={18} color="#f97316" />
          Announcements
        </h3>
      </div>

      {loading ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#64748b',
          padding: '8px 0',
          fontSize: '14px',
        }}>
          <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
          Loading...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {visibleCampaigns.map(campaign => {
            const theme = getThemeStyles(campaign.theme);
            const ThemeIcon = theme.icon;

            return (
              <div
                key={campaign.campaign_id}
                style={{
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '12px',
                  padding: '12px',
                  position: 'relative',
                }}
              >
                {/* Dismiss */}
                {campaign.dismissible && (
                  <button
                    onClick={() => handleDismiss(campaign.campaign_id)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'transparent',
                      border: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      opacity: 0.5,
                    }}
                  >
                    <X size={14} color="#64748b" />
                  </button>
                )}

                {/* Content */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  }}>
                    <ThemeIcon size={18} color={theme.color} />
                  </div>

                  <div style={{ flex: 1, minWidth: 0, paddingRight: campaign.dismissible ? '16px' : 0 }}>
                    <h4 style={{
                      margin: '0 0 2px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#1e293b',
                    }}>
                      {campaign.title}
                    </h4>
                    {campaign.subtitle && (
                      <p style={{
                        margin: '0 0 8px',
                        fontSize: '13px',
                        color: '#475569',
                        lineHeight: 1.4,
                      }}>
                        {campaign.subtitle}
                      </p>
                    )}

                    {campaign.cta_text && (
                      <button
                        onClick={() => handleCTAClick(campaign)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '6px 12px',
                          background: theme.color,
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        {campaign.cta_text}
                        {campaign.cta_url?.startsWith('http') ? (
                          <ExternalLink size={12} />
                        ) : (
                          <ChevronRight size={12} />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
